import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { api } from '@/lib/api'
import { EmptyState } from '@/components/shared/EmptyState'
import type { Contact } from '@/types'
import { Users, Plus, Trash2, Pencil, Loader2, Search } from 'lucide-react'

export function Contacts() {
  const [contacts, setContacts] = useState<Contact[]>([])
  const [loading, setLoading] = useState(true)
  const [addOpen, setAddOpen] = useState(false)
  const [editingContact, setEditingContact] = useState<Contact | null>(null)
  const [search, setSearch] = useState('')
  const [form, setForm] = useState({ name: '', email: '' })
  const [saving, setSaving] = useState(false)

  const fetchContacts = async () => {
    try {
      const res = await api.get('/contacts')
      setContacts(res.data.contacts || [])
    } catch {
      toast.error('Failed to load contacts')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchContacts()
  }, [])

  const handleSave = async () => {
    const email = form.email.trim().toLowerCase()
    if (!email) {
      toast.error('Email is required')
      return
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Please enter a valid email address')
      return
    }
    const payload = { name: form.name.trim() || null, email }
    setSaving(true)
    try {
      if (editingContact) {
        await api.put(`/contacts/${editingContact.id}`, payload)
        toast.success('Contact updated')
        setEditingContact(null)
      } else {
        await api.post('/contacts', payload)
        toast.success('Contact added')
      }
      setForm({ name: '', email: '' })
      setAddOpen(false)
      fetchContacts()
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to save contact')
    } finally {
      setSaving(false)
    }
  }

  const openEdit = (contact: Contact) => {
    setEditingContact(contact)
    setForm({ name: contact.name || '', email: contact.email })
    setAddOpen(true)
  }

  const openAdd = () => {
    setEditingContact(null)
    setForm({ name: '', email: '' })
    setAddOpen(true)
  }

  const handleDialogClose = (open: boolean) => {
    setAddOpen(open)
    if (!open) {
      setEditingContact(null)
      setForm({ name: '', email: '' })
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this contact?')) return
    try {
      await api.delete(`/contacts/${id}`)
      setContacts((prev) => prev.filter((c) => c.id !== id))
      toast.success('Contact deleted')
    } catch {
      toast.error('Failed to delete contact')
    }
  }

  const filtered = contacts.filter(
    (c) =>
      c.email.toLowerCase().includes(search.toLowerCase()) ||
      (c.name && c.name.toLowerCase().includes(search.toLowerCase()))
  )

  if (loading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-8 w-32 bg-accent rounded" />
        <div className="h-64 bg-accent rounded-xl" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Contacts</h1>
          <p className="text-sm text-muted-foreground mt-1">People and mailboxes you email often</p>
        </div>
        <Button onClick={openAdd}>
          <Plus className="mr-2 h-4 w-4" />
          Add Contact
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search contacts..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {contacts.length === 0 ? (
        <EmptyState
          title="No contacts yet"
          description="Add contacts so they appear in the compose autocomplete."
          icon={<Users className="h-5 w-5" />}
          action={
            <Button onClick={openAdd}>
              <Plus className="mr-2 h-4 w-4" />
              Add Contact
            </Button>
          }
        />
      ) : (
        <div className="grid gap-2">
          {filtered.map((contact) => (
            <Card key={contact.id}>
              <CardContent className="p-3 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">{contact.name || contact.email}</p>
                  {contact.name && <p className="text-xs text-muted-foreground">{contact.email}</p>}
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => openEdit(contact)}
                    aria-label={`Edit ${contact.name || contact.email}`}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-destructive"
                    onClick={() => handleDelete(contact.id)}
                    aria-label={`Delete ${contact.name || contact.email}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={addOpen} onOpenChange={handleDialogClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingContact ? 'Edit Contact' : 'Add Contact'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="name">Name (optional)</Label>
              <Input
                id="name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Jane Doe"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="jane@example.com"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => handleDialogClose(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editingContact ? 'Update' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
